import { computed, Ref } from 'vue'
import useCurrentUserProfile from '@/modules/user/compositions/useCurrentUserProfile'
import { CallTriage, InvitedCaster } from '@/types/casts'
import { useCurrentTeam } from '@/modules/teams/compositions'
import { Cast } from '../classes'
import { isFanRoomCast } from '@/modules/casts/utils/isCastType'
import useCurrentCast from './currentCast'

const useCurrentInvitedCaster = (argCast?: Ref<Cast|null>) => {

  const { currentUserId, currentUserProfile } = useCurrentUserProfile()
  const { currentTeamMembers } = useCurrentTeam()

  const cast = computed(() => argCast?.value ?? useCurrentCast().currentCast.value ?? null)
  const invitedCasters = computed(() => cast.value?.invited_casters ?? {})
  const invitedCastersQueued = computed(() => Object.values(invitedCasters.value)
    .filter((caster) => caster.triage === CallTriage.Queued && !caster.deleted)
    .sort((a, b) => a.timestamp - b.timestamp))

  const currentInvitedCaster = computed<InvitedCaster|null>(() => {
    const id = currentUserProfile.value?.assignedId ?? currentUserId.value
    return (id && invitedCasters.value[id]) ? invitedCasters.value[id] : null
  })

  const currentTriage = computed(() => currentInvitedCaster.value?.triage ?? CallTriage.Init)

  const currentQueuePosition = computed(() => {
    if (invitedCastersQueued.value.length <= 0) return 0
    return invitedCastersQueued.value.findIndex((caster) => caster.userId === currentInvitedCaster.value?.userId) + 1
  })

  const moderatorUserProfile = computed(() => {
    if (!currentInvitedCaster.value?.moderator) return null
    return currentTeamMembers.value[currentInvitedCaster.value.moderator] ?? null
  })

  const isParticipant = computed(() => {
    const id = currentUserProfile.value?.assignedId ?? currentUserId.value
    return id && isFanRoomCast(cast.value) && !!cast.value.participants[id]
  })

  const hasParticipantInfo = computed(() => {
    const id = currentUserProfile.value?.assignedId ?? currentUserId.value
    if (!isFanRoomCast(cast.value)) return false
    if (!id || !cast.value.participants[id]) return false
    return !!cast.value.participants[id].hasAllRequiredInfo && !cast.value.participants[id].deleted
  })

  return {
    currentInvitedCaster,
    currentQueuePosition,
    currentTriage,
    isAnonymous: computed(() => !!currentInvitedCaster.value?.anonymous),
    isAccepted: computed(() => currentTriage.value === CallTriage.Accepted),
    isInit: computed(() => currentTriage.value === CallTriage.Init),
    isInModeration: computed(() => currentTriage.value === CallTriage.InModeration),
    isInvitedCaster: computed(() => !!currentInvitedCaster.value && !currentInvitedCaster.value.deleted),
    isParticipant,
    hasParticipantInfo,
    isQueued: computed(() => currentTriage.value === CallTriage.Queued),
    isRejected: computed(() => currentTriage.value === CallTriage.Rejected),
    isSelfTest: computed(() => currentTriage.value === CallTriage.Selftest),
    isUnverified: computed(() => currentTriage.value === CallTriage.Unverified),
    moderatorUserProfile
  }
}

export default useCurrentInvitedCaster
